import { Component, ComponentChild, ComponentConstructor, h, toChildArray } from 'preact';
import { Properties } from '../common';
import { WithStyles, withStyles } from '../style';
import { isFunction, isPresent, isServer } from '../utils';
import { MODAL_ANIMATION_DURATION, MODAL_OPEN_CLASS, ModalContext, ModalStatus } from './types';
import * as styles from './ModalProvider.scss';
import * as modalStyles from './components.scss';

export interface ModalProviderProps extends Properties {}
type EffectiveProps = ModalProviderProps & WithStyles;

export interface ModalProviderState {
  modalStatus: ModalStatus;
  modal?: ComponentChild;
  focusTarget?: HTMLElement;
  onClose?: () => any;
}

@withStyles(styles, modalStyles)
export class ModalProvider extends Component<ModalProviderProps, ModalProviderState> {
  state = { modalStatus: ModalStatus.CLOSED } as ModalProviderState;

  showModal = (modal: ComponentConstructor, onClose?: () => any): void => {
    if (isServer() || this.state.modalStatus !== ModalStatus.CLOSED) {
      return;
    }
    document.documentElement.classList.add(MODAL_OPEN_CLASS);
    this.setState(() => ({
      modalStatus: ModalStatus.OPEN,
      modal,
      onClose,
      focusTarget: document.activeElement as HTMLElement,
    }));
  };

  hideModal = (): void => {
    if (isServer() || this.state.modalStatus !== ModalStatus.OPEN) {
      return;
    }

    this.setState((prevState) => ({ ...prevState, modalStatus: ModalStatus.CLOSING }));
    document.documentElement.classList.remove(MODAL_OPEN_CLASS);

    if (isFunction(this.state.onClose)) {
      this.state.onClose();
    }

    setTimeout(() => {
      const { focusTarget } = this.state;
      if (isPresent(focusTarget) && document.body.contains(focusTarget)) {
        focusTarget.focus();
      }
      this.setState((prevState) => ({
        ...prevState,
        modalStatus: ModalStatus.CLOSED,
        modal: undefined,
        onClose: undefined,
        focusTarget: undefined,
      }));
    }, MODAL_ANIMATION_DURATION);
  };

  getChildContext(): ModalContext {
    return {
      modal: {
        showModal: this.showModal,
        hideModal: this.hideModal,
        horizontal: true,
      },
    };
  }

  componentWillUnmount(): void {
    this.hideModal();
  }

  render({ children, tx }: EffectiveProps, { modal }: ModalProviderState): h.JSX.Element {
    const childArray = toChildArray(children);
    return (
      <div class={tx(this.props.class, 'relative')}>
        {childArray && childArray[0]}
        <div>{modal}</div>
      </div>
    );
  }
}
